import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import MuiBreadCrumbs, {
  MuiBreadCrumbsProps,
} from 'web-library/stories/molecule/BreadCrumbs/MuiBreadCrumbs';
import Box from 'web-library/stories/atom/Box/Box';
import HeaderTemplate from 'web-library/stories/template/HeaderTemplate/HeaderTemplate';
import Link from 'web-library/stories/atom/Link/Link';
import Typography from 'web-library/stories/atom/Typography/Typography';

import { makeStyles } from 'tss-react/mui';
import { updateEmptyState } from '../../store/appSlice';
import RouteKey from '../../routes/key';

const { error: errorRoute, auth: authRoute } = RouteKey.child;
const { otp } = RouteKey.auth.child;

import {typographyTheme} from "web-library/theme/helper/tssHelper"
const useStyles = makeStyles()((theme) =>
({
  root: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  linkRoot: {
     ...typographyTheme(theme, 'body2'),
    color: theme.color.link.linkActive01,
  },
  txtRoot: {
    color: theme.color.text.textPrimary,
  },
}));

const useBasePath = () => {
  const location = useLocation();
  const params = useParams<Record<string, string>>();
  return {
    path: Object.values(params).reduce(
      (path, param) => path.replace(`/${param}`, ''),
      location.pathname
    ),
    location,
  };
};

const emptyRoute = [errorRoute, authRoute];

const renderBreadCrumb = (
  index: number,
  matchedChildLocationName: string[],
  locationNameList: string[],
  classes: any,
  breadcrumbText: string,
  urlParams: {
    history: any;
    location: any;
    pathName: string;
  },
  rootName: string = 'Home'
) => {
  const { state } = urlParams.location;
  // to check whether its last path name
  // const last = index === lastIndex;
  // to go the sub route
  const lastIndex = matchedChildLocationName.length - 1;
  const to = `/${locationNameList.slice(0, index + 1).join('/')}`;
  // if first index, render home path
  if (index === 0) {
    return (
      <Link href="/" key={urlParams.pathName} className={classes.linkRoot}>
        {rootName}
      </Link>
    );
  }

  return urlParams.pathName === matchedChildLocationName[lastIndex] ? (
    <Typography key={to} variant="body2" color="textPrimary" className={classes.txtRoot}>
      {breadcrumbText}
    </Typography>
  ) : (
      <Link
          onClick={() => urlParams.history.push(to, state || {})}
          key={urlParams.pathName}
          className={classes.linkRoot}
      >
        {breadcrumbText}
      </Link>
  );
};

/** reuseable breadcrumbs component
 * */
interface BreadCrumbsProps extends MuiBreadCrumbsProps {
  rootName?: string;
}

const BreadCrumbs: React.FC<BreadCrumbsProps> = (props: BreadCrumbsProps) => {
  const { rootName } = props;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { separator = 'NextIcon', ...others } = props;
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { path, location } = useBasePath();
  const history = useHistory();

  const locationNameList: string[] = path
    .replace(/\/$/, '')
    .split('/')
    .filter((x) => x);

  useEffect(() => {
    dispatch(
      updateEmptyState(
        emptyRoute.includes(locationNameList[0]) ||
        (locationNameList[0] === authRoute && locationNameList[1] === otp)
      )
    );
  }, [locationNameList, dispatch]);

  // ignore breadcrumbs if error pages
  if (locationNameList[0] === 'error' || locationNameList[0] === 'not-support') {
    return null;
  }

  /** to render bread crumbs itmes
   * */
  const renderBreadCrumbsItems = () => {
    if (
      locationNameList.length > 1 &&
      (RouteKey[locationNameList[0] as keyof typeof RouteKey] as { child: any }).child
    ) {
      const childRoutes = (RouteKey[locationNameList[0] as keyof typeof RouteKey] as { child: any })
        .child;
      const routesToTitles: { [key in string]: string } = Object.keys(childRoutes).reduce(
        (prevValue, currentValue) => {
          const currentRouteInfo = childRoutes[currentValue];
          return { ...prevValue, [currentRouteInfo.route]: currentRouteInfo.title };
        },
        {}
      );
      const matchedChildLocationName = locationNameList
        .slice(0)
        .filter((locationName) => routesToTitles[locationName]);
      return locationNameList.map((pathName, index) => {
        const currentRouteSegment = locationNameList[index];
        const breadcrumbText = routesToTitles[currentRouteSegment];
        if (!breadcrumbText && index !== 0) {
          return null;
        }

        return renderBreadCrumb(
          index,
          matchedChildLocationName,
          locationNameList,
          classes,
          breadcrumbText,
          {
            history,
            location,
            pathName,
          },
          rootName
        );
      });
    }
    return '';
  };

  return (
    <HeaderTemplate>
      <Box className={classes.root}>
        <MuiBreadCrumbs {...others}>{renderBreadCrumbsItems()}</MuiBreadCrumbs>
      </Box>
    </HeaderTemplate>
  );
};
export default BreadCrumbs;
